import React from "react";
import { Item } from "semantic-ui-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import company from "../../icons/company.svg";
import location from "../../icons/location.svg";
import person from "../../icons/person.svg";
import date from "../../icons/date.svg";
import { Internship } from "./types";
import { UserRoles } from "../../app/auth/roles";
import { prettifyDate, prettifyDateWithoutTime } from "../../app/helpers/dateHelpers";
import { HasRole } from "../../app/helpers/userHelpers";
import Multiline from "../../components/Multiline";
import InternshipCapacity from "./InternshipCapacity";
import IndustryTags from "../industries/IndustryTags";
import { Industry } from "../industries/types";
import InternshipItemActions from "./InternshipItemActions";
import agent from "../../app/api/agent";
import useToaster from "../../app/hooks/useToaster";
import { useAppDispatch } from "../../app/hooks/useAppDispatch";
import { userActions } from "../user/actions";

interface Props {
  internship: Internship;
  hideLogo?: boolean;
  allIndustries: Industry[];
  onUpdate?: () => void;
  showContactPerson?: boolean;
}

const InternshipItem = ({ internship, hideLogo, allIndustries, onUpdate, showContactPerson = false }: Props) => {
  const { t } = useTranslation();
  const { success, apiError } = useToaster();
  const dispatch = useAppDispatch();

  const isAdmin = HasRole(UserRoles.admin);
  const isInactive = internship.endDate ? new Date(internship.endDate) < new Date() : false;
  const isFull = internship.isClosed || internship.occupiedApplications >= internship.participantsCapacity;

  const handleApplicationCancel = (applicationId: number) => {
    agent.Applications.cancel(applicationId)
      .then(() => {
        success(t("application.notifications.success.cancel"));
        dispatch(userActions.getCurrentUser());
        onUpdate?.();
      })
      .catch(() => {
        apiError(t("common.errors.general"));
      });
  };

  const renderLocation = () => {
    return internship.remote ? t("shadowing.remote") : `${internship.city}, ${internship.address}`;
  };

  const itemClass = [
    "shadowing-list-item",
    isInactive ? "shadowing-list-item--inactive" : "",
    !isInactive && isFull ? "shadowing-list-item--full" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Item className={itemClass}>
      {!hideLogo && <Item.Image src={internship.company.logoUrl || "/assets/logo_placeholder.png"} alt="logo" />}
      <Item.Content>
        <Item.Header>
          {internship.title}
          {!isInactive && isFull && <span className="shadowing-list-item__full-badge">Pilnai užimta</span>}
          {showContactPerson && internship.responsiblePerson && (
            <span className="shadowing-list-item__contact-name">
              {internship.responsiblePerson.firstName} {internship.responsiblePerson.surname}
            </span>
          )}
        </Item.Header>
        <Item.Meta>
          <span>
            <img src={location} alt="location" />
            {renderLocation()}
          </span>
          <span>
            <img src={company} alt="company" />
            {internship.company.name}
          </span>
          <span>
            <img src={person} alt="person" />
            <InternshipCapacity internship={internship} />
          </span>
          <span>
            <img src={date} alt="date" />
            {prettifyDateWithoutTime(internship.startDate)}
            {internship.endDate ? ` – ${prettifyDateWithoutTime(internship.endDate)}` : ""}
          </span>
        </Item.Meta>
        <Item.Description>
          <Multiline value={internship.description} />
          {internship.industryIds && (
            <IndustryTags industryIds={internship.industryIds} allIndustries={allIndustries} />
          )}
          <Link to={`/companies/${internship.company.id}`}>{t("internship.more_company_internships")}</Link>
        </Item.Description>
        <Item.Extra className="shadowing-item-extra">
          <InternshipItemActions
            internship={internship}
            handleApplicationCancel={handleApplicationCancel}
            onUpdate={onUpdate}
          />
          {isAdmin && (
            <div className="shadowing-item-extra__date">
              {t("shadowing.created_on")}
              {": "}
              <span>{prettifyDate(internship.createdOn)}</span>
            </div>
          )}
        </Item.Extra>
      </Item.Content>
    </Item>
  );
};

export default InternshipItem;
